package co.maytes.standupbot.dashboard.pages.app.onboarding

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.layout.AuthGuard
import co.maytes.standupbot.dashboard.components.widgets.Avatar
import co.maytes.standupbot.dashboard.components.widgets.AvatarTone
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonSize
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideCheck
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideShield
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideUsers
import co.maytes.standupbot.dashboard.features.onboarding.OnboardingStep
import co.maytes.standupbot.dashboard.features.onboarding.Seal
import co.maytes.standupbot.dashboard.features.onboarding.WizardFrame
import co.maytes.standupbot.dashboard.features.onboarding.WizardList
import co.maytes.standupbot.dashboard.features.onboarding.WizardSection
import co.maytes.standupbot.dashboard.features.onboarding.WizardStage
import co.maytes.standupbot.dashboard.features.onboarding.WizardStepper
import co.maytes.standupbot.dashboard.features.onboarding.rememberOnboardingStore
import co.maytes.standupbot.dashboard.features.roster.LinkReportState
import co.maytes.standupbot.dashboard.features.roster.MemberLinkChips
import co.maytes.standupbot.dashboard.features.roster.rememberLinkReportStore
import co.maytes.standupbot.dashboard.features.settings.LinkLinearIdentityModal
import co.maytes.standupbot.dashboard.features.setup.SetupCheckReportView
import co.maytes.standupbot.dashboard.features.setup.TestMySetupButton
import co.maytes.standupbot.dashboard.features.standups.rememberStandupsStore
import co.maytes.standupbot.dashboard.lib.api.Result
import co.maytes.standupbot.dashboard.stores.ToastKind
import co.maytes.standupbot.dashboard.stores.rememberApiClient
import co.maytes.standupbot.dashboard.stores.rememberAuthStore
import co.maytes.standupbot.dashboard.stores.rememberSetupCheckStore
import co.maytes.standupbot.dashboard.stores.rememberSetupStatusStore
import co.maytes.standupbot.dashboard.stores.rememberUiStore
import co.maytes.standupbot.dashboard.styles.Motion
import co.maytes.standupbot.shared.dto.common.PagedResponse
import co.maytes.standupbot.shared.dto.standup.LinkStateDto
import co.maytes.standupbot.shared.dto.standup.MemberLinkReportDto
import co.maytes.standupbot.shared.dto.standup.StandupMemberDto
import co.maytes.standupbot.shared.dto.standuprun.StandupRunSummaryDto
import co.maytes.standupbot.shared.roster.LinkReportText
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.navigation.Link
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Page("/app/onboarding/done")
@Composable
fun OnboardingDonePage() = AuthGuard {
    WizardStage {
        DoneContent()
    }
}

@Composable
private fun DoneContent() {
    val ctx = rememberPageContext()
    val api = rememberApiClient()
    val ui = rememberUiStore()
    val auth = rememberAuthStore()
    val store = rememberOnboardingStore()
    val standups = rememberStandupsStore()
    val reportStore = rememberLinkReportStore()
    val scope = rememberCoroutineScope()

    val workspaceId = auth.currentUser?.workspace?.id
    val standupId = store.createdStandupId
    val runId = store.previewRunId
    val channelLabel = store.selectedChannelName?.let { "#$it" } ?: "your channel"

    var run by remember { mutableStateOf<StandupRunSummaryDto?>(null) }
    var scheduleHuman by remember { mutableStateOf<String?>(null) }
    var pickFor by remember { mutableStateOf<StandupMemberDto?>(null) }

    LaunchedEffect(workspaceId, standupId) {
        if (workspaceId == null || standupId == null) return@LaunchedEffect
        (standups.detail(workspaceId, standupId) as? Result.Ok)?.let {
            scheduleHuman = "${it.value.scheduleHuman} (${it.value.timezone})"
        }
        standups.members(workspaceId, standupId)
        reportStore.load(workspaceId, standupId)
        if (runId != null) {
            val runs = api.get<PagedResponse<StandupRunSummaryDto>>(
                "/api/v1/workspaces/$workspaceId/standups/$standupId/runs?limit=1",
            )
            run = (runs as? Result.Ok)?.value?.items?.firstOrNull { it.id == runId }
        }
    }

    val setupCheck = rememberSetupCheckStore()
    val setupStatus = rememberSetupStatusStore()
    LaunchedEffect(workspaceId, auth.isAdmin) {
        val ws = workspaceId
        if (auth.isAdmin && !ws.isNullOrBlank() && setupCheck.state.report == null && setupCheck.run(ws)) setupStatus.refresh(ws)
    }

    val members = standupId?.let { standups.cachedMembers(it) }.orEmpty()
    val reportState = reportStore.state
    val slackUrl = run?.slackMessageUrl

    WizardFrame(
        title = "You're all set",
        subline = if (runId != null) {
            "Your first standup went to $channelLabel. From here it posts on schedule."
        } else {
            "Your standup will land in $channelLabel on schedule."
        },
        stepper = { WizardStepper(current = OnboardingStep.ordered.size) },
        art = { Seal(large = true) },
        footerStart = if (slackUrl != null) {
            { Link(path = slackUrl, text = "View in Slack") }
        } else {
            null
        },
        footerEnd = {
            Button(
                label = "Go to dashboard",
                variant = if (runId != null) ButtonVariant.Secondary else ButtonVariant.Primary,
                onClick = { ctx.router.navigateTo("/app") },
            )
            if (runId != null) {
                Button(
                    label = "Open the run",
                    variant = ButtonVariant.Primary,
                    onClick = { ctx.router.navigateTo("/app/runs/$runId") },
                )
            }
        },
    ) {
        if (auth.isAdmin) {
            WizardSection(
                title = "Setup check",
                action = { TestMySetupButton() },
                icon = { LucideShield(size = 18) },
            ) {
                SetupCheckReportView()
            }
        }

        if (standupId != null) {
            WizardSection(title = "Next steps", icon = { LucideUsers(size = 18) }) {
                WizardList {
                    key(scheduleHuman != null) {
                        ChecklistRow(
                            done = true,
                            title = "Standup scheduled",
                            note = scheduleHuman ?: "Loading the schedule",
                            loading = scheduleHuman == null,
                        )
                    }
                    LinkReportRows(
                        state = reportState,
                        members = members,
                        onPick = { pickFor = it },
                        onRetry = {
                            if (workspaceId != null) scope.launch { reportStore.load(workspaceId, standupId) }
                        },
                    )
                    ChecklistRow(
                        done = false,
                        title = "Invite teammates to the dashboard",
                        note = "Being on the roster does not give anyone a login.",
                        trailing = { Link(path = "/app/settings/members", text = "Invite teammates") },
                    )
                }
            }
        }
    }

    pickFor?.let { member ->
        LinkLinearIdentityModal(
            displayName = member.displayName,
            currentLinearUserId = member.linearUserId,
            onSave = { linearUserId ->
                pickFor = null
                if (linearUserId != null && workspaceId != null && standupId != null) {
                    scope.launch {
                        when (val r = standups.linkLinearIdentity(workspaceId, standupId, member.userId, linearUserId)) {
                            is Result.Ok -> {
                                ui.showToast("Linked ${member.displayName} to Linear", ToastKind.Success)
                                reportStore.load(workspaceId, standupId)
                            }
                            is Result.Err -> ui.showToast("Couldn't link: ${r.error.displayMessage}", ToastKind.Error)
                        }
                    }
                }
            },
            onDismiss = { pickFor = null },
        )
    }
}

@Composable
private fun ChecklistRow(
    done: Boolean,
    title: String,
    note: String? = null,
    loading: Boolean = false,
    trailing: (@Composable () -> Unit)? = null,
) {
    Div(attrs = { classes("sb-wiz-row", Motion.ENTER_CLASS) }) {
        Div(attrs = { classes("sb-wiz-tick"); if (done) classes("sb-wiz-tick--done") }) {
            if (done) LucideCheck(size = 10)
        }
        Div(attrs = { classes("sb-wiz-row-text") }) {
            Span { Text(title) }
            if (loading) {
                Div(attrs = { classes("sb-wiz-bar"); attr("aria-hidden", "true") })
            } else {
                note?.let { Span(attrs = { classes("sb-wiz-row-note") }) { Text(it) } }
            }
        }
        trailing?.invoke()
    }
}

@Composable
private fun LinkReportRows(
    state: LinkReportState,
    members: List<StandupMemberDto>,
    onPick: (StandupMemberDto) -> Unit,
    onRetry: () -> Unit,
) {
    key(state::class) {
        when (state) {
            LinkReportState.Idle, LinkReportState.Loading ->
                ChecklistRow(done = false, title = "Linear and GitHub links", loading = true)
            is LinkReportState.Failed ->
                ChecklistRow(
                    done = false,
                    title = "Couldn't check the Linear and GitHub links",
                    note = state.message,
                    trailing = {
                        Button(label = "Retry", variant = ButtonVariant.Secondary, size = ButtonSize.Sm, onClick = onRetry)
                    },
                )
            is LinkReportState.Ready -> LinkReportReady(report = state.report, members = members, onPick = onPick)
        }
    }
}

@Composable
private fun LinkReportReady(
    report: MemberLinkReportDto,
    members: List<StandupMemberDto>,
    onPick: (StandupMemberDto) -> Unit,
) {
    val needFix = LinkReportText.needsLinearFix(report)
    val githubOff = report.members.isNotEmpty() && report.members.all { it.github == LinkStateDto.NOT_CONNECTED }
    ChecklistRow(
        done = needFix.isEmpty() && report.members.isNotEmpty(),
        title = LinkReportText.summary(report),
        note = if (needFix.isNotEmpty()) "These people have no Linear activity in the standup until they are linked." else null,
    )
    needFix.forEach { status ->
        val member = members.firstOrNull { it.userId == status.userId }
        Div(attrs = { classes("sb-wiz-row", Motion.ENTER_CLASS) }) {
            Avatar(
                name = member?.displayName ?: "?",
                src = member?.avatarUrl,
                sizePx = 28,
                tone = AvatarTone.Accent,
            )
            Div(attrs = { classes("sb-wiz-row-text") }) {
                Span { Text(member?.displayName ?: "Unknown member") }
            }
            MemberLinkChips(status = status)
            if (member != null) {
                Button(
                    label = "Pick person",
                    variant = ButtonVariant.Secondary,
                    size = ButtonSize.Sm,
                    onClick = { onPick(member) },
                )
            }
        }
    }
    if (githubOff) {
        ChecklistRow(
            done = false,
            title = "GitHub isn't connected",
            note = "Pull requests are not included.",
            trailing = { Link(path = "/app/settings/integrations", text = "Connect GitHub") },
        )
    }
}
